<template>
  <section id="hero" class="relative w-full h-screen">
    <!-- Slider Container -->
    <div class="relative h-full w-full overflow-hidden" @touchstart="handleTouchStart" @touchmove="handleTouchMove"
      @touchend="handleTouchEnd">
      <transition-group name="fade">
        <!-- Current Slide -->
        <div v-for="(slide, index) in slides" :key="slide.id" v-show="currentIndex === index" class="absolute inset-0">
          <!-- Image -->
          <img :src="slide.image" :alt="$t(slide.title)" class="h-full w-full object-cover blur-[2px]" draggable="false" />
          <!-- Text Overlay -->
          <div class="absolute inset-0 bg-black bg-opacity-60">
            <div class="flex h-full items-center justify-center px-4">
              <div class="text-center">
                <h2 class="text-4xl md:text-6xl font-bold text-white smmob">{{ $t(slide.title) }}</h2>
                <p v-if="slide.subtitle" class="mt-4 text-xl md:text-2xl text-white">{{ $t(slide.subtitle) }}</p>
              </div>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Navigation Buttons -->
      <button @click="prev" :class="[
        'absolute top-1/2 -translate-y-1/2 group',
        $i18n.locale === 'ar' ? 'right-4' : 'left-4'
      ]">
        <div class="flex items-center space-x-2">
          <div
            class="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center shadow-lg backdrop-blur-sm transform transition-all duration-300 group-hover:scale-110 group-hover:bg-white">
            <i :class="[
              'fas transition-colors duration-300 text-gray-800 group-hover:text-green-600',
              $i18n.locale === 'ar' ? 'fa-chevron-right' : 'fa-chevron-left'
            ]"></i>
          </div>
        </div>
      </button>
      <button @click="next" :class="[
        'absolute top-1/2 -translate-y-1/2 group',
        $i18n.locale === 'ar' ? 'left-4' : 'right-4'
      ]">
        <div class="flex items-center space-x-2">
          <div
            class="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center shadow-lg backdrop-blur-sm transform transition-all duration-300 group-hover:scale-110 group-hover:bg-white">
            <i :class="[
              'fas transition-colors duration-300 text-gray-800 group-hover:text-green-600',
              $i18n.locale === 'ar' ? 'fa-chevron-left' : 'fa-chevron-right'
            ]"></i>
          </div>
        </div>
      </button>

      <!-- Progress Bar -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-3"
        :class="{ 'space-x-reverse': $i18n.locale === 'ar' }">
        <div v-for="(slide, index) in slides" :key="slide.id" @click="goToSlide(index)" class="group cursor-pointer">
          <div class="relative h-2 w-12 rounded-full overflow-hidden bg-white/30">
            <div class="absolute inset-y-0 left-0 bg-white transition-all duration-300 rounded-full" :class="[
              currentIndex === index ? 'w-full' : 'w-0 group-hover:w-full',
              currentIndex === index ? '' : 'group-hover:bg-white/50'
            ]"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "HeroSection",
  data() {
    return {
      currentIndex: 0,
      slides: [
        {
          id: 1,
          title: "hero.slide1.title",
          image: require('@/assets/img/vecteezy_smartphone-gps-navigation-icon-and-map-on-traffic_29551699.webp')
        },
        {
          id: 2,
          title: "hero.slide2.title",
          image: require('@/assets/img/vecteezy_smartphone-gps-navigation-icon-and-map-on-traffic_29551676.webp')
        },
        {
          id: 3,
          title: "hero.slide3.title",
          image: require('@/assets/img/car-with-mobile.webp')
        }
      ],
      interval: null,
      touchStartX: 0,
      touchEndX: 0,
    };
  },
  mounted() {
    this.startSlideshow();
  },
  beforeUnmount() {
    this.stopSlideshow();
  },
  methods: {
    startSlideshow() {
      this.interval = setInterval(this.next, 5000);
    },
    stopSlideshow() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    },
    resetSlideshow() {
      this.stopSlideshow();
      this.startSlideshow();
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.resetSlideshow();
    },
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.resetSlideshow();
    },
    goToSlide(index) {
      this.currentIndex = index;
      this.resetSlideshow();
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
    },
    handleTouchMove(e) {
      this.touchEndX = e.touches[0].clientX;
    },
    handleTouchEnd() {
      const swipeThreshold = 50;
      const swipeDistance = this.touchEndX - this.touchStartX;

      if (Math.abs(swipeDistance) > swipeThreshold) {
        if (swipeDistance > 0) {
          this.prev();
        } else {
          this.next();
        }
      }

      this.touchStartX = 0;
      this.touchEndX = 0;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

[dir="rtl"] .space-x-2 > :not([hidden]) ~ :not([hidden]) {
  --tw-space-x-reverse: 1;
}

[dir="rtl"] .space-x-3 > :not([hidden]) ~ :not([hidden]) {
  --tw-space-x-reverse: 1;
}

@media (max-width: 640px) {
  .w-12 {
    width: 2.5rem;
  }

  .h-12 {
    height: 2.5rem;
  }
}

@media (max-width: 768px) {
  .smmob {
    font-size: 2.0rem;
    padding: 0 40px;
  }
}
</style>